import { useTranslation } from "@/hooks/useTranslation";
import { extractArtifactTextTail, isArtifactProduced } from "@/lib/artifact";
import { cn } from "@/lib/utils";
import {
  chatArtifactPanelStore,
  useChatArtifactPanel,
} from "@/stores/chat-artifact-panel-store";
import { ToolUIPart } from "ai";
import { AlertCircleIcon, ChevronRightIcon, FileTextIcon } from "lucide-react";

/** Characters of the latest text shown while a document is being written. */
const WRITING_TEXT_TAIL_CHARS = 160;

type ChatToolShowArtifactProps = {
  /** `showArtifact` writes a new document, `editArtifact` changes one. */
  readonly kind?: "show" | "edit";
  readonly state: ToolUIPart["state"];
  /** The call's arguments, partial while they stream in. */
  readonly input?: unknown;
  readonly output: unknown;
};

/**
 * Names the Artifact the assistant produced (or updated), on one compact
 * line, and toggles the side panel on click: it opens (or switches to) this
 * Artifact, or closes the panel when this Artifact is already the one shown.
 *
 * While the call is still being written, it says so, with the document's
 * title and latest text as they arrive — as plain text, never as HTML: the
 * streamed markup is only sanitized once finished, server-side.
 */
export default function ChatToolShowArtifact({
  kind = "show",
  state,
  input,
  output,
}: ChatToolShowArtifactProps) {
  const { t } = useTranslation();
  const shownArtifact = useChatArtifactPanel();

  if (state === "input-streaming" || state === "input-available") {
    const { title, html } = (input ?? {}) as {
      title?: unknown;
      html?: unknown;
    };
    const textTail =
      kind === "show" && typeof html === "string"
        ? extractArtifactTextTail(html, WRITING_TEXT_TAIL_CHARS)
        : "";
    return (
      <div
        aria-live="polite"
        className="mb-2 flex w-full flex-col gap-1.5 rounded-xl border bg-background p-3 not-prose"
        data-state="writing"
      >
        <div className="flex items-center gap-2">
          <FileTextIcon className="size-4 shrink-0 animate-pulse text-primary motion-reduce:animate-none" />
          <span className="text-sm text-muted-foreground">
            {t(
              kind === "edit"
                ? "chat.message.tool.updatingArtifact"
                : "chat.message.tool.writingArtifact"
            )}
          </span>
          {typeof title === "string" && title && (
            <span className="truncate text-sm font-medium">{title}</span>
          )}
        </div>
        {textTail && (
          <p
            aria-hidden="true"
            className="line-clamp-2 text-xs text-muted-foreground/80 [mask-image:linear-gradient(to_right,transparent,black_2.5rem)]"
          >
            {textTail}
          </p>
        )}
      </div>
    );
  }

  // An error state is a call that never got an output — typically one cut
  // while its document was still being written — and falls through to the
  // same failure marker as a refusal.
  if (state !== "output-available" && state !== "output-error") {
    return null;
  }

  if (state === "output-available" && isArtifactProduced(output)) {
    const isShown = shownArtifact?.artifactId === output.artifactId;
    return (
      <button
        type="button"
        aria-expanded={isShown}
        onClick={() =>
          isShown
            ? chatArtifactPanelStore.close()
            : chatArtifactPanelStore.open(output)
        }
        className={cn(
          "group mb-2 flex w-full items-center gap-2 rounded-xl border bg-background p-3 text-left not-prose transition-[background-color,border-color,transform] duration-150 ease-out hover:bg-muted/50 active:scale-[0.98] motion-reduce:active:scale-100",
          // Marked by its border and a soft ring, never by a tint: the chat
          // behind it is already muted, and a tinted card vanishes into it.
          isShown && "border-primary/50 ring-2 ring-primary/10",
        )}
      >
        <FileTextIcon className="size-4 shrink-0 text-primary" />
        <span className="text-sm text-muted-foreground">
          {t(
            kind === "edit"
              ? "chat.message.tool.editArtifact"
              : "chat.message.tool.showArtifact"
          )}
        </span>
        <span className="truncate text-sm font-medium">{output.title}</span>
        <ChevronRightIcon
          aria-hidden="true"
          // Turned around while the panel is open: a click now folds it back.
          className={cn(
            "ml-auto size-4 shrink-0 text-muted-foreground transition-transform duration-150 ease-out motion-reduce:transition-none",
            isShown
              ? "rotate-180 group-hover:-translate-x-0.5"
              : "group-hover:translate-x-0.5",
          )}
        />
      </button>
    );
  }

  // A refused edit is the model's to retry, and usually is, in the same turn:
  // a failure marker per attempt would be noise over a document that did
  // update. The model reports a failure it cannot fix in its own text.
  if (kind === "edit") {
    return null;
  }

  // A refusal (invalid HTML, a failed upload): the tool's own message already
  // tells the model to report the failure in its own text, but a document
  // generation failing is worth a sober, explicit marker too — not a card to
  // click, nothing to open.
  return (
    <div className="mb-2 flex w-full items-center gap-2 rounded-xl border border-dashed p-3 not-prose">
      <AlertCircleIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">
        {t("chat.message.tool.showArtifactFailed")}
      </span>
    </div>
  );
}
